import { API, compass } from '@resclick/compass';
import { IUser, IUserRole } from '@resclick/data-access';
import { Action, Module, Mutation, VuexModule } from 'vuex-module-decorators';

interface IUserStore {
  profile: Partial<Omit<IUser, 'preferences'>>;
  preferences: Partial<IUser['preferences']>;
  role: IUserRole;
}

const createProfile = (): Partial<IUser> => ({
  name: '',
  surname: '',
  email: '',
  id: '',
  role: '',
});

const createPreferences = (): Partial<IUser['preferences']> => ({
  locale: 'en-US',
  currency: 'USD',
});

@Module({ namespaced: true, preserveState: true })
class UserStore extends VuexModule<IUserStore> {
  profile = createProfile();
  preferences = createPreferences();

  get defaultCurrency() {
    return this.preferences?.currency || 'USD';
  }

  get currentCompany() {
    return this.profile.company;
  }

  get ownedPermissions() {
    return (this.profile.role as IUserRole)?.permissions || [];
  }

  @Mutation
  public SET_USER(data: IUser): void {
    const { preferences, ...profile } = data;
    this.profile = profile;
    this.preferences = preferences;
  }

  @Mutation
  public CLEAR_USER() {
    this.profile = createProfile();
    this.preferences = createPreferences();
  }

  @Action({ commit: 'SET_USER' })
  public async getProfile(id: string) {
    try {
      const { data: user } = await compass.do('users', 'findOne', {
        params: { id },
      });

      const { data: role } = await compass.do('userRoles', 'findOne', {
        params: { id: user.role },
      });

      user.role = role;

      return user;
    } catch (e) {
      this.context.commit('CLEAR_USER');
    }
  }

  @Action
  public async updateProfile(data: InstanceType<API['users']['update']['dto']>) {
    await compass.do('users', 'update', {
      params: { id: this.profile.id as string },
      data: data,
    });
    await this.context.dispatch('getProfile', this.profile.id);
  }

  @Action({ rawError: true })
  public async updatePreferences(data: InstanceType<API['users']['update']['dto']>) {
    const update = JSON.parse(JSON.stringify(this.profile));
    delete update.role;
    await compass.do('users', 'update', {
      params: { id: this.profile.id as string },
      data: {
        ...update,
        preferences: data as any,
      },
    });
    await this.context.dispatch('getProfile', this.profile.id);
  }
}

export default UserStore;
